/**
 * REST API client with JWT auth interceptor.
 *
 * Handles token refresh automatically when a 401 is received.
 */

import type { TokenResponse } from "./types";

// In pywebview, window.location.origin may not resolve correctly for relative URLs.
// Use absolute URL based on current origin, or fall back to the known server port.
const BASE_URL = typeof window !== "undefined" && window.location.origin.includes("127.0.0.1")
  ? `${window.location.origin}/api`
  : "/api";

let accessToken: string | null = null;
let refreshToken: string | null = null;
let onAuthExpired: (() => void) | null = null;

/**
 * True only when running inside the native pywebview desktop shell, where the
 * backend is local and OS-level actions ("open in the default app", "reveal in
 * the file manager") actually dispatch. In any browser — the cloud web app, or
 * the local ``--no-window`` fallback — those actions can't work, so the UI must
 * offer browser open / download instead and hide the file-manager affordances.
 * Read defensively so a missing global never throws.
 */
export function isNativeHost(): boolean {
  return (
    typeof window !== "undefined" &&
    !!(window as unknown as { pywebview?: unknown }).pywebview
  );
}

// Whether this (native) box has an OS file-dialog tool. ``null`` until probed.
// The composer must know this BEFORE the click: on ChromeOS Crostini (no zenity)
// the picker has to be the hidden <input>, clicked SYNCHRONOUSLY — invoking
// /api/files/pick first and clicking only after the round-trip forfeits WebKit's
// user-activation and the file selection comes back empty (no attachment pill,
// 2026-07-21). Probing up front keeps the native OS dialog on Linux-desktop /
// macOS / Windows while ChromeOS falls straight to a synchronous browser picker.
let nativeDialog: boolean | null = null;
let nativeDialogProbe: Promise<void> | null = null;

/** True/false once probed, else null. Read synchronously by the composer. */
export function nativeDialogAvailable(): boolean | null {
  return nativeDialog;
}

/** Probe once (native only). Idempotent; safe to call from every composer mount.
 *  On the web there's no OS dialog, so it stays null and the composer uses the
 *  synchronous browser picker anyway. */
export function probeNativeDialog(): void {
  if (!isNativeHost() || nativeDialogProbe) return;
  nativeDialogProbe = fetch("/api/files/pick?probe=1")
    .then((r) => (r.ok ? r.json() : { available: false }))
    .then((d) => { nativeDialog = !!d.available; })
    .catch(() => { nativeDialog = false; });
}

/** Initialize auth state from stored tokens */
export function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem("access_token", access);
  localStorage.setItem("refresh_token", refresh);
}

/** Load tokens from localStorage on startup */
export function loadStoredTokens(): boolean {
  accessToken = localStorage.getItem("access_token");
  refreshToken = localStorage.getItem("refresh_token");
  return !!accessToken;
}

/** Clear tokens (logout) */
export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
}

/** Get current access token (for WebSocket auth) */
export function getAccessToken(): string | null {
  return accessToken;
}

/** Set callback for when auth expires completely */
export function onAuthExpiredCallback(cb: () => void) {
  onAuthExpired = cb;
}

/** Attempt to refresh the access token */
async function tryRefresh(): Promise<boolean> {
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) return false;

    const data: TokenResponse = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

/** Make an authenticated API request */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (!headers.has("Content-Type") && options.body && typeof options.body === "string") {
    headers.set("Content-Type", "application/json");
  }

  // API responses are auth-dependent and must never be served from the HTTP
  // cache: a browser that once fetched an /api path the backend didn't have yet
  // (mixed frontend/backend versions during a rollout) heuristically caches the
  // SPA fallback's index.html *as* that API response and keeps replaying it
  // after the endpoint ships.
  let res = await fetch(`${BASE_URL}${path}`, { ...options, headers, cache: "no-store" });

  // Auto-refresh on 401
  if (res.status === 401 && refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers.set("Authorization", `Bearer ${accessToken}`);
      res = await fetch(`${BASE_URL}${path}`, { ...options, headers, cache: "no-store" });
    } else {
      clearTokens();
      onAuthExpired?.();
      throw new Error("Session expired. Please sign in again.");
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const message = formatErrorDetail(body?.detail) || `API error: ${res.status}`;
    throw new ApiError(message, res.status, body?.detail);
  }

  return res.json();
}

/**
 * Error thrown by `apiFetch` for non-2xx responses. Carries the original
 * structured `detail` payload from the server so callers that need to
 * branch on error shape (e.g., field-level validation lists) can do so
 * without re-parsing the message string.
 */
/** Upload helper for FormData endpoints (/api/files/drop): bearer + ONE
 *  refresh-retry on 401. Raw fetches never refreshed, so a >24h-old tab's
 *  uploads all 401'd until reload ("uploaded docs can't be accessed"). */
export async function authedUpload(url: string, form: FormData): Promise<Response> {
  const attempt = () =>
    fetch(url, {
      method: "POST",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body: form,
    });
  let res = await attempt();
  if (res.status === 401 && refreshToken && (await tryRefresh())) {
    res = await attempt();
  }
  return res;
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Coerce FastAPI's `detail` field into a single human-readable string.
 *
 * `detail` may be any of:
 *   - a string (most of our 4xx handlers)
 *   - an object like `{code, errors: [...]}` (skills `INVALID_SKILL`)
 *   - an array of `{loc, msg, type}` (FastAPI 422 validation)
 *
 * Without this, `new Error(detail)` would stringify objects to
 * "[object Object]" and surface that to users.
 */
function formatErrorDetail(detail: unknown): string {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) =>
        typeof d === "string"
          ? d
          : (d as { msg?: string })?.msg ?? JSON.stringify(d),
      )
      .join("; ");
  }
  if (typeof detail === "object") {
    const obj = detail as Record<string, unknown>;
    if (Array.isArray(obj.errors)) {
      return (obj.errors as unknown[]).map(String).join("; ");
    }
    if (typeof obj.message === "string") return obj.message;
    if (typeof obj.error === "string") return obj.error;
    return JSON.stringify(detail);
  }
  return String(detail);
}
